// src/pages/AdminFundSharing.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../user/Dashboard.css";

/* --------------- helpers --------------- */
const isLocal = window.location.hostname === "localhost";
const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
const api = axios.create({ baseURL: baseUrl });
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function LoadingModal({ text = "Loading..." }) {
  return (
    <div style={styles.modal}>
      <div style={styles.modalCard}>
        <div style={styles.spinner} />
        <p>{text}</p>
      </div>
    </div>
  );
}

/* --------------- main page --------------- */
export default function AdminFundSharing() {
  const navigate = useNavigate();

  // bots
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState("");
  const selectedBot = useMemo(
    () => bots.find((b) => b._id === selectedBotId) || null,
    [bots, selectedBotId]
  );

  // create-bot inline form
  const [newBotName, setNewBotName] = useState("");
  const [newBotExchange, setNewBotExchange] = useState("bitget"); // "bitget" | "binance"
  const [creating, setCreating] = useState(false);

  // admin summary & per-user table
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [entryValues, setEntryValues] = useState({}); // { [userId]: { deposit, withdraw } }

  // editable bot figures
  const [tradingAmount, setTradingAmount] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");

  /* ---- load bots, then choose one ---- */
  async function loadBots() {
    try {
      const res = await api.get("/api/fund-sharing/bots", { headers: authHeaders() });
      const list = res.data?.bots || [];
      setBots(list);
      if (list.length > 0) {
        // keep current selection if still present, otherwise pick the first
        setSelectedBotId((prev) => (list.some((b) => b._id === prev) ? prev : list[0]._id));
      } else {
        setSelectedBotId("");
      }
    } catch (e) {
      console.error("[admin] loadBots error:", e);
      setBots([]);
      setSelectedBotId("");
    }
  }

  /* ---- load summary & users for selected bot ---- */
  async function loadSummaryAndUsers(botId) {
    if (!botId) {
      setSummary(null);
      setAllUsers([]);
      return;
    }
    setLoading(true);
    try {
      const [sumRes, usersRes] = await Promise.all([
        api.get("/api/fund-sharing/admin/summary", {
          headers: authHeaders(),
          params: { botId },
        }),
        api.get("/api/fund-sharing/admin/users", {
          headers: authHeaders(),
          params: { botId },
        }),
      ]);
      setSummary(sumRes.data || null);
      setAllUsers(usersRes.data?.rows || []);
      // seed editable bot fields
      setTradingAmount(sumRes.data?.bot?.tradingAmount ?? "");
      setCurrentValue(sumRes.data?.bot?.currentValue ?? "");
      setPeriodLabel(sumRes.data?.bot?.periodLabel ?? "");
    } catch (e) {
      console.error("[admin] loadSummary/users error:", e?.response?.data || e.message);
      setSummary(null);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      await loadBots();
    })();
  }, []);

  useEffect(() => {
    if (selectedBotId) loadSummaryAndUsers(selectedBotId);
  }, [selectedBotId]);

  /* ---- create bot ---- */
  async function createBot() {
    if (!newBotName.trim()) {
      alert("Please enter a bot name.");
      return;
    }
    setCreating(true);
    try {
      await api.post(
        "/api/fund-sharing/bot",
        { name: newBotName.trim(), exchange: newBotExchange },
        { headers: authHeaders() }
      );
      setNewBotName("");
      await loadBots(); // will auto-select the first
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  }

  /* ---- save bot figures ---- */
  async function saveBot() {
    if (!selectedBotId) return;
    try {
      await api.post(
        "/api/fund-sharing/bot",
        {
          _id: selectedBotId,
          tradingAmount: tradingAmount === "" ? undefined : Number(tradingAmount),
          currentValue: currentValue === "" ? undefined : Number(currentValue),
          periodLabel,
        },
        { headers: authHeaders() }
      );
      await loadSummaryAndUsers(selectedBotId);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    }
  }

  /* ---- per-user row edit helpers ---- */
  function setRowValue(userId, field, value) {
    setEntryValues((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [field]: value },
    }));
  }

  async function saveRow(user, field) {
    const val = entryValues[user.userId]?.[field];
    if (!val || Number(val) <= 0) return;
    if (!selectedBotId) {
      alert("Pick a bot first.");
      return;
    }
    try {
      await api.post(
        "/api/fund-sharing/contributions",
        {
          botId: selectedBotId,
          userId: user.userId,
          type: field === "deposit" ? "deposit" : "withdraw",
          amount: Number(val),
        },
        { headers: authHeaders() }
      );
      // clear field and refresh
      setEntryValues((prev) => ({
        ...prev,
        [user.userId]: { ...(prev[user.userId] || {}), [field]: "" },
      }));
      await loadSummaryAndUsers(selectedBotId);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    }
  }

  async function resetUser(userId) {
    if (!selectedBotId) return;
    if (!window.confirm("Reset ALL entries for this user on this bot?")) return;
    try {
      await api.post(`/api/fund-sharing/admin/user/${userId}/reset`, null, {
        headers: authHeaders(),
      });
      await loadSummaryAndUsers(selectedBotId);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    }
  }

  /* --------------- UI --------------- */

  // No bots yet → show create form only
  if (!bots.length) {
    return (
      <div className="dashboard-main">
        <div className="outcome-table-wrapper-dashboard">
          <div className="outcome-table-bots">
            <h3>🤖 Create Your First Bot</h3>
            <div style={{ display: "grid", gap: 10, maxWidth: 420 }}>
              <label>
                <div style={styles.label}>Bot Name</div>
                <input
                  className="date-input-wallet"
                  value={newBotName}
                  onChange={(e) => setNewBotName(e.target.value)}
                  placeholder="e.g., Bitget Main"
                />
              </label>
              <label>
                <div style={styles.label}>Exchange</div>
                <select
                  className="date-input-wallet"
                  value={newBotExchange}
                  onChange={(e) => setNewBotExchange(e.target.value)}
                >
                  <option value="bitget">Bitget</option>
                  <option value="binance">Binance</option>
                </select>
              </label>
              <div>
                <button onClick={createBot} disabled={creating}>
                  {creating ? "Creating..." : "Create Bot"}
                </button>
              </div>
              <p style={{ opacity: 0.8 }}>
                After creating a bot, you’ll be able to set Trading Amount / Current Value and
                allocate user funds to it.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Bots exist → show selector + summary
  if (loading || !summary) {
    return <LoadingModal text="Loading admin summary..." />;
  }

  const totalActive = summary?.totalActive || 0;

  return (
<div className="dashboard-main">
  <div className="fs-container">
    {/* Bot Picker */}
    <div className="fs-card">
      <h3 className="fs-section-title">🤖 Select Bot</h3>

      <div className="fs-row fs-actions">
        <select
          className="fs-input"
          value={selectedBotId}
          onChange={(e) => setSelectedBotId(e.target.value)}
        >
          {bots.map((b) => (
            <option key={b._id} value={b._id}>
              {b.name} ({b.exchange})
            </option>
          ))}
        </select>
        <button onClick={loadBots}>Refresh Bots</button>
        <button onClick={() => setCreating(true)}>➕ Add New Bot</button>
      </div>

      {creating && (
        <div className="fs-grid">
          <label>
            <div className="fs-label">Bot Name</div>
            <input
              className="fs-input"
              value={newBotName}
              onChange={(e) => setNewBotName(e.target.value)}
              placeholder="e.g., Binance Alpha"
            />
          </label>
          <label>
            <div className="fs-label">Exchange</div>
            <select
              className="fs-input"
              value={newBotExchange}
              onChange={(e) => setNewBotExchange(e.target.value)}
            >
              <option value="bitget">Bitget</option>
              <option value="binance">Binance</option>
            </select>
          </label>
          <div className="fs-row fs-actions">
            <button onClick={createBot} disabled={!newBotName.trim()}>
              Create
            </button>
            <button className="fs-btn-ghost" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {/* Bot Controls */}
    <div className="fs-card">
      <h3 className="fs-section-title">
        ⚙️ Bot Controls — {summary?.bot?.name} ({summary?.bot?.exchange})
      </h3>

      <div className="fs-grid">
        <label>
          <div className="fs-label">Trading amount (USDT)</div>
          <input
            type="number"
            step="0.01"
            value={tradingAmount}
            onChange={(e) => setTradingAmount(e.target.value)}
            className="fs-input"
          />
        </label>
        <label>
          <div className="fs-label">Current value (USDT)</div>
          <input
            type="number"
            step="0.01"
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            className="fs-input"
          />
        </label>
        <label className="fs-col-span-2">
          <div className="fs-label">Period Label (optional)</div>
          <input
            value={periodLabel}
            onChange={(e) => setPeriodLabel(e.target.value)}
            className="fs-input"
            placeholder="e.g., Sep 2025"
          />
        </label>

        <div className="fs-row fs-actions">
          <button onClick={saveBot}>Save Bot</button>
        </div>

        <div className="fs-metrics">
          <div><strong>Total Active Capital:</strong> {money(totalActive)}</div>
          <div>
            <strong>Bot PnL (current - trading):</strong> {money(summary.bot?.periodRealizedPnl)} ({summary.bot?.periodLabel || '-'})
          </div>
          <div>
            <small>Updated: {summary.bot?.updatedAt ? new Date(summary.bot.updatedAt).toLocaleString() : '-'}</small>
          </div>
        </div>
      </div>
    </div>

        {/* Users table */}
        <div className="outcome-table">
          <h3>👥 Users (Allocate / Take Profit)</h3>
          <div className="table-scroll-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Deposits</th>
                  <th>Withdrawals</th>
                  <th>
                    Net <span style={{ opacity: 0.6, fontWeight: 400 }}>(used for share)</span>
                  </th>
                  <th style={{ minWidth: 180 }}>Allocated → Deposit</th>
                  <th style={{ minWidth: 180 }}>Taken out → Withdraw</th>
                  <th>Save</th>
                  <th>Details</th>
                  <th>Reset</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", opacity: 0.7 }}>
                      No users found.
                    </td>
                  </tr>
                )}

                {allUsers.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.userName || "(no name)"}</td>
                    <td>{money(u.deposits)}</td>
                    <td>{money(u.withdrawals)}</td>
                    <td>{money(u.net)}</td>

                    {/* deposit */}
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={entryValues[u.userId]?.deposit || ""}
                        onChange={(e) => setRowValue(u.userId, "deposit", e.target.value)}
                        className="date-input-wallet"
                        placeholder="Amount"
                      />
                    </td>

                    {/* withdraw */}
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={entryValues[u.userId]?.withdraw || ""}
                        onChange={(e) => setRowValue(u.userId, "withdraw", e.target.value)}
                        className="date-input-wallet"
                        placeholder="Amount"
                      />
                    </td>

                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => saveRow(u, "deposit")}
                          disabled={!entryValues[u.userId]?.deposit}
                        >
                          Save Deposit
                        </button>
                        <button
                          onClick={() => saveRow(u, "withdraw")}
                          disabled={!entryValues[u.userId]?.withdraw}
                        >
                          Save Withdraw
                        </button>
                      </div>
                    </td>

                    <td>
                      <button onClick={() => navigate(`/admin/fund-sharing/user/${u.userId}?botId=${selectedBotId}`)}>
                        View
                      </button>
                    </td>

                    <td>
                      <button
                        onClick={() => resetUser(u.userId)}
                        style={{ background: "#462", borderColor: "#6a4" }}
                        title="Reset all entries for this user on this bot"
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ opacity: 0.75, marginTop: 10 }}>
            Tip: After any changes, the Admin summary and each user’s page will recalculate shares automatically.
          </p>
        </div>
      </div>
    </div>
  );
}

/* --------------- inline styles --------------- */
const styles = {
  modal: {
    position: "fixed",
    inset: 0,
    background: "rgba(10,10,20,.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modalCard: {
    background: "#1e1f2f",
    padding: "24px 28px",
    borderRadius: 12,
    boxShadow: "0 0 20px rgba(0,0,0,.4)",
    textAlign: "left",
    color: "#fff",
  },
  spinner: {
    border: "4px solid rgba(255,255,255,.2)",
    borderTop: "4px solid #00f7ff",
    borderRadius: "50%",
    width: 40,
    height: 40,
    margin: "0 auto 12px",
    animation: "spin 1s linear infinite",
  },
  label: { display: "block", marginBottom: 4, fontSize: 13, opacity: 0.85 },
};
